/**
 * AuthBootstrap — restores the session on app start from the httpOnly refresh
 * cookie. Calls POST /auth/refresh once; on success it hydrates the auth store
 * with a fresh access token + user, otherwise it leaves the app unauthenticated
 * (the AuthGuard then redirects to /login). Children render only after this
 * attempt resolves, so guards never flash the login page on a valid session.
 */
import { useEffect, type ReactNode } from 'react';
import { useAuthStore } from '@app/store/auth.store';
import { authService } from '@/services';

interface AuthBootstrapProps {
  children: ReactNode;
}

export function AuthBootstrap({ children }: AuthBootstrapProps) {
  const bootstrapped = useAuthStore((s) => s.bootstrapped);
  const login = useAuthStore((s) => s.login);
  const setBootstrapped = useAuthStore((s) => s.setBootstrapped);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authService.refresh();
        if (!cancelled) {
          login(res.user, res.accessToken, res.user.tenantId ?? null);
        }
      } catch {
        // No valid refresh cookie — remain unauthenticated.
      } finally {
        if (!cancelled) setBootstrapped(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [login, setBootstrapped]);

  if (!bootstrapped) {
    return (
      <div
        className="flex h-screen items-center justify-center"
        style={{ backgroundColor: 'var(--color-background)' }}
        role="status"
        aria-label="Loading session…"
      >
        <div className="h-10 w-10 rounded-xl bg-primary/20 animate-pulse" />
      </div>
    );
  }

  return <>{children}</>;
}
